import AddOnService from '@spike/addon-service-model';
import { AppointmentCountRecurrent, TrainingMetadata, WalkingMetadata } from '@spike/appointment-model';
import { NewBookingDiscount } from '@spike/new-booking-model';
import {
    INITIAL_BOOKING_EDIT_SCOPE_REQUEST,
    NewBookingsStatus,
    recurrentRescheduleThunk,
    singleRescheduleThunk,
    updateAppointmentThunk
} from '@spike/new-bookings-action';
import { AppointmentOrderItem, Discount, Order, Orderizable, OrderizableTypes, RedeemTypes } from '@spike/order-model';
import { isOrderInRecurringSeries } from './isOrderInRecurringSeries';
import {
    INITIAL_ORDER_EDIT_SCOPE_REQUEST,
    OrdersStatus,
    reloadOrderThunk,
    saveOrderItemThunk,
    updateOrderItemScopedThunk
} from '@spike/orders-action';
import { Product } from '@spike/product-model';
import { Vehicle } from '@spike/vehicle-model';
import useNonInitialEffect from '@versiondos/hooks';
import ComplementaryItem from 'components/CreateBooking/ExtraItems';
import PetAndServices from 'components/CreateBooking/PetAndServices';
import { TransportationType } from 'components/CreateBooking/Transportation/UI/TransportationTypeSelector/TransportationTypeSelector';
import { useApiClientWrapper, useMarketplace, useMasterData, useTimeZone } from 'hooks';
import { cloneDeep } from 'lodash';
import uniq from 'lodash/uniq';
import {
    AddOnDate,
    Feeding,
    Medication,
    itemTypes,
    Discount as MultipleBookingDiscount,
    PetServiceWrapper,
    versionPetAndServices,
    Daycare,
    Boarding,
    Walking,
    Training,
    Transportation
} from 'model/NewMultipleBooking';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import ConfirmRecurringRescheduleDialog from './UI/ConfirmRecurringRescheduleDialog';
import { getRoomUnitId } from 'components/CreateBooking/CreateBooking';
import {
    resolveBoardingOrderItemPriceForSave,
    resolveDaycareSavePricing,
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    resolveStandardServiceSavePricing
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import {
    customPriceDataFromBoarding,
    customPriceDataFromDaycare,
    customPriceDataFromPetService,
    customPriceDataFromTraining,
    customPriceDataFromTransportation,
    customPriceDataFromWalking,
    persistOrderItemCustomPrices
} from 'components/CreateBooking/utils/bookingCustomPrices';
import { Task } from '@spike/task-model';
import { createTaskDate, createMedicationTaskDate } from 'utils/dateTimeUtils';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';
import {
    BoardingOffer,
    TrainingService,
    WalkingService,
    BoardingService,
    SessionBookingType,
    resolveBookingType
} from 'model/Service';
import { getTrainingOrWalkingDurationMinutes } from 'utils/appointmentServiceDuration';

export interface EditOrderItemProps {
    order: Order;
    orderItemId: number;
    recurrentInfo?: AppointmentCountRecurrent;
    hideRemove?: boolean;
    zIndex?: number;
    className?: string;
    onClose: () => void;
    onCloseOrderDetail?: () => void;
    onSaveSuccess?: () => void;
    onDelete?: () => void;
    onRebook?: (appointmentItem: AppointmentOrderItem) => void;
}

export const convertDiscount = (discount?: Discount): MultipleBookingDiscount => {
    if (discount?.percentage) {
        return {
            type: 'percentage',
            value: Number(discount.percentage)
        };
    } else if (discount?.amount) {
        return {
            type: 'amount',
            value: Number(discount.amount)
        };
    } else {
        return {
            type: 'none',
            value: 0
        };
    }
};

const isSpecificTimeTask = (task: Task): boolean =>
    (task.feeding as { specific_time?: boolean; specificTime?: boolean })?.specific_time === true ||
    (task.feeding as { specific_time?: boolean; specificTime?: boolean })?.specificTime === true;

const formatTaskDateToTimeString = (taskDate: Moment | string, timezone: string): string =>
    (moment.isMoment(taskDate) ? taskDate.clone() : moment(taskDate)).tz(timezone).format('h:mma').toLowerCase();

export type FeedingSlotTimes = { morning: string; afternoon: string; evening: string };

/** True si la hora de la tarea coincide con el slot predeterminado del negocio para ese momento del día. */
const taskMatchesPresetFeedingSlot = (task: Task, tz: string, slots: FeedingSlotTimes): boolean => {
    const momentId = task.feeding?.feedingMomentTypeId;
    if (!momentId || !task.taskDate) return true;
    const slotStr =
        momentId === 1 ? slots.morning : momentId === 2 ? slots.afternoon : momentId === 3 ? slots.evening : '';
    if (!slotStr?.trim()) return true;
    const dayLocal = (moment.isMoment(task.taskDate) ? task.taskDate.clone() : moment(task.taskDate))
        .clone()
        .tz(tz)
        .startOf('day');
    const preset = createTaskDate(dayLocal, slotStr, tz);
    const td = (moment.isMoment(task.taskDate) ? task.taskDate.clone() : moment(task.taskDate)).clone().tz(tz);
    return td.format('YYYY-MM-DD HH:mm') === preset.format('YYYY-MM-DD HH:mm');
};

const getSpecificFeedingMomentIds = (f: Feeding): number[] =>
    f.momentTypes && f.momentTypes.length > 0 ? f.momentTypes : Object.keys(f.specificMomentTimes || {}).map(Number);

const feedingIsSpecificTimeOnSave = (f: Feeding): boolean =>
    f.feedingScheduleType === 'specificTime' &&
    getSpecificFeedingMomentIds(f).some(m => !!String(f.specificMomentTimes?.[m] ?? '').trim());

const transformToFeedings = (
    tasks: Task[],
    timezone?: string,
    flow?: 'grooming' | 'daycare' | 'boarding',
    from?: Moment,
    to?: Moment,
    feedingSlots?: FeedingSlotTimes
): Feeding[] => {
    const feedingTasks = tasks.filter(task => task.feeding?.feedingMomentTypeId);

    const groupedTasks = new Map<string, Task[]>();

    feedingTasks.forEach(task => {
        const key = `${task.feeding?.foodTypeId || ''}-${task.feeding?.foodQuantity || ''}-${
            task.feeding?.foodQuantityUnitId || ''
        }-${task.feeding?.feedingInstructions || ''}`;

        if (!groupedTasks.has(key)) {
            groupedTasks.set(key, []);
        }
        groupedTasks.get(key)!.push(task);
    });

    const feedings: Feeding[] = [];
    const tz = timezone || 'UTC';

    Array.from(groupedTasks.values()).forEach(taskGroup => {
        const firstTask = taskGroup[0];
        const hasSpecificTimeFlag = taskGroup.some(isSpecificTimeTask);
        const allTasksMatchPresetSlots =
            !!feedingSlots &&
            taskGroup.length > 0 &&
            taskGroup.every(t => taskMatchesPresetFeedingSlot(t, tz, feedingSlots));
        const inferredSpecificTime = !!feedingSlots && taskGroup.length > 0 && !allTasksMatchPresetSlots;
        const isSpecificTime = hasSpecificTimeFlag || inferredSpecificTime;

        const momentTypeToTaskIdMap = new Map<number, number[]>();
        taskGroup.forEach(task => {
            if (task.feeding?.feedingMomentTypeId && task.id) {
                const ids = momentTypeToTaskIdMap.get(task.feeding.feedingMomentTypeId) || [];
                ids.push(task.id);
                momentTypeToTaskIdMap.set(task.feeding.feedingMomentTypeId, ids);
            }
        });

        const momentTypes = taskGroup.map(task => task.feeding?.feedingMomentTypeId!).filter(id => id !== undefined);
        const uniqueMomentTypes = uniq(momentTypes);

        let specificMomentTimes: Record<number, string> | undefined;
        if (isSpecificTime) {
            specificMomentTimes = {};
            uniqueMomentTypes.forEach(momentTypeId => {
                const taskForMoment = taskGroup.find(t => t.feeding?.feedingMomentTypeId === momentTypeId);
                if (taskForMoment?.taskDate) {
                    specificMomentTimes![momentTypeId] = formatTaskDateToTimeString(taskForMoment.taskDate, tz);
                }
            });
            if (Object.keys(specificMomentTimes).length === 0) specificMomentTimes = undefined;
        }

        feedings.push({
            taskIds: taskGroup.map(task => task.id!).filter(id => id !== undefined),
            momentTypes: uniqueMomentTypes,
            momentTypeToTaskIdMap,
            feedingScheduleType: isSpecificTime ? 'specificTime' : 'feedingMoment',
            ...(specificMomentTimes && { specificMomentTimes }),
            foodProvisionType: firstTask.feeding?.foodProvisionerTypeId!,
            foodType: firstTask.feeding?.foodTypeId,
            quantity: {
                quantity: firstTask.feeding?.foodQuantity ? parseFloat(firstTask.feeding?.foodQuantity) : undefined,
                unit: firstTask.feeding?.foodQuantityUnitId
            },
            brand: firstTask.feeding?.feedingBrand,
            instructions: firstTask.feeding?.feedingInstructions
        });
    });

    return feedings;
};

const transformToMedications = (
    tasks: Task[],
    timezone: string,
    flow: 'grooming' | 'daycare' | 'boarding',
    from: Moment,
    to: Moment
): Medication[] => {
    const medicationTasks = tasks.filter(task => task.medication?.medicationName);

    const groupedTasks = new Map<string, Task[]>();

    medicationTasks.forEach(task => {
        const key = `${task.medication?.medicationName || ''}-${task.medication?.medicationMethodId || ''}-${
            task.medication?.dosageAmount || ''
        }-${task.medication?.dosageUnitId || ''}`;

        if (!groupedTasks.has(key)) {
            groupedTasks.set(key, []);
        }
        groupedTasks.get(key)!.push(task);
    });

    const medications: Medication[] = [];

    groupedTasks.forEach(taskGroup => {
        const firstTask = taskGroup[0];

        if (flow === 'daycare' || flow === 'boarding') {
            const scheduleToTaskIdMap = new Map<string, number[]>();
            taskGroup.forEach(task => {
                if (task.taskDate && task.id) {
                    const scheduleTime = task.taskDate.clone().tz(timezone).format('h:mmA');
                    const ids = scheduleToTaskIdMap.get(scheduleTime) || [];
                    ids.push(task.id);
                    scheduleToTaskIdMap.set(scheduleTime, ids);
                }
            });

            const tasksByDate = new Map<string, Task[]>();
            taskGroup.forEach(task => {
                if (task.taskDate) {
                    const dateKey = task.taskDate.clone().tz(timezone).format('YYYY-MM-DD');
                    if (!tasksByDate.has(dateKey)) {
                        tasksByDate.set(dateKey, []);
                    }
                    tasksByDate.get(dateKey)!.push(task);
                }
            });

            const stayDays: string[] = [];
            const current = from.clone();
            while (current.isSameOrBefore(to, 'day')) {
                stayDays.push(current.format('YYYY-MM-DD'));
                current.add(1, 'day');
            }

            const stayFromDay = from.format('YYYY-MM-DD');
            const stayToDay = to.format('YYYY-MM-DD');

            const filteredTasksByDate = new Map<string, Task[]>();
            tasksByDate.forEach((dayTasks, dateKey) => {
                const filteredTasks = dayTasks.filter(task => {
                    if (!task.taskDate) return false;

                    if (dateKey === stayFromDay && task.taskDate.isBefore(from)) {
                        return false;
                    }

                    if (dateKey === stayToDay && task.taskDate.isSameOrAfter(to)) {
                        return false;
                    }

                    return true;
                });

                if (filteredTasks.length > 0) {
                    filteredTasksByDate.set(dateKey, filteredTasks);
                }
            });

            const dosesPerDay = stayDays.map(day => {
                const dayTasks = filteredTasksByDate.get(day) || [];
                return Array.from(
                    new Set(
                        dayTasks
                            .map(task => task.taskDate?.clone().tz(timezone).format('h:mmA') || '')
                            .filter(time => time !== '')
                    )
                ).sort();
            });

            const allDaysHaveSameDoses =
                dosesPerDay.length > 0 &&
                dosesPerDay.every(
                    doses =>
                        doses.length === dosesPerDay[0].length &&
                        doses.every((dose, idx) => dose === dosesPerDay[0][idx])
                );

            const hasTasksForAllDays = stayDays.every(day => filteredTasksByDate.has(day));

            const isStrictlyDaily = hasTasksForAllDays && allDaysHaveSameDoses;

            const isAlmostDaily = (() => {
                if (stayDays.length < 3) return false;

                const dosePatternCounts = new Map<string, number>();
                dosesPerDay.forEach(doses => {
                    const pattern = doses.join(',');
                    dosePatternCounts.set(pattern, (dosePatternCounts.get(pattern) || 0) + 1);
                });

                let mostCommonPattern = '';
                let maxCount = 0;
                dosePatternCounts.forEach((count, pattern) => {
                    if (count > maxCount) {
                        maxCount = count;
                        mostCommonPattern = pattern;
                    }
                });

                const daysWithDifferentPattern = dosesPerDay.filter(
                    doses => doses.join(',') !== mostCommonPattern
                ).length;

                return daysWithDifferentPattern <= 1 && mostCommonPattern !== '';
            })();

            const isDaily = isStrictlyDaily || isAlmostDaily;

            if (isDaily) {
                medications.push({
                    taskIds: taskGroup.map(task => task.id!).filter(id => id !== undefined),
                    schedule: dosesPerDay[0] || [],
                    scheduleToTaskIdMap,
                    name: firstTask.medication?.medicationName || '',
                    methodType: firstTask.medication?.medicationMethodId,
                    dosage: {
                        quantity: firstTask.medication?.dosageAmount
                            ? parseFloat(firstTask.medication?.dosageAmount)
                            : undefined,
                        unit: firstTask.medication?.dosageUnitId
                    },
                    instructions: firstTask.medication?.medicationInstructions,
                    specificSchedule: []
                });
            } else {
                const specificSchedule = stayDays.map(day => {
                    const dayTasks = tasksByDate.get(day) || [];
                    const doses = Array.from(
                        new Set(
                            dayTasks
                                .map(task => task.taskDate?.clone().tz(timezone).format('h:mmA') || '')
                                .filter(time => time !== '')
                        )
                    ).sort();

                    return {
                        date: moment.tz(day, 'YYYY-MM-DD', timezone),
                        enabled: doses.length > 0,
                        doses: doses.length > 0 ? doses : []
                    };
                });

                medications.push({
                    taskIds: taskGroup.map(task => task.id!).filter(id => id !== undefined),
                    schedule: [],
                    scheduleToTaskIdMap,
                    name: firstTask.medication?.medicationName || '',
                    methodType: firstTask.medication?.medicationMethodId,
                    dosage: {
                        quantity: firstTask.medication?.dosageAmount
                            ? parseFloat(firstTask.medication?.dosageAmount)
                            : undefined,
                        unit: firstTask.medication?.dosageUnitId
                    },
                    instructions: firstTask.medication?.medicationInstructions,
                    specificSchedule
                });
            }
        }
    });

    return medications;
};

const wrapService = (
    appointmentItem: AppointmentOrderItem,
    timezone: string,
    feedingSlots?: FeedingSlotTimes,
    vehicle?: Vehicle
): PetServiceWrapper => {
    const isEnrollment = appointmentItem.orderItem.redeem?.type === RedeemTypes.ENROLLMENT;
    const beforePrice = Number(appointmentItem.orderItem.originalPrice ?? appointmentItem.orderItem.amount);
    const amount = Number(appointmentItem.orderItem.amount);
    const isFreeByEnrollment = isEnrollment && amount === 0 && Number.isFinite(beforePrice) && beforePrice > 0;
    const businessAreaId = appointmentItem.appointment.services[0].businessArea.id;

    return {
        beforePrice,
        service: appointmentItem.appointment.services[0],
        price: isFreeByEnrollment ? beforePrice : amount,
        durationInMinutes:
            businessAreaId === 'walking' || businessAreaId === 'training'
                ? getTrainingOrWalkingDurationMinutes(appointmentItem.appointment, businessAreaId)
                : appointmentItem.appointment.duration.to
                      .clone()
                      .diff(appointmentItem.appointment.duration.from.clone(), 'minutes'),
        feedings: transformToFeedings(
            appointmentItem.appointment.tasks,
            timezone,
            businessAreaId as 'grooming' | 'daycare' | 'boarding',
            appointmentItem.appointment.duration.from,
            appointmentItem.appointment.duration.to,
            feedingSlots
        ),
        medications: transformToMedications(
            appointmentItem.appointment.tasks,
            timezone,
            businessAreaId as 'grooming' | 'daycare' | 'boarding',
            appointmentItem.appointment.duration.from,
            appointmentItem.appointment.duration.to
        ),
        roomUnitId: appointmentItem.appointment.roomUnitId,
        businessArea: businessAreaId as 'grooming' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training',
        color: appointmentItem.appointment.services[0].color,
        bookingType: appointmentItem.appointment?.details?.training?.sessionType,
        inHomeService:
            appointmentItem.appointment?.details?.training?.type === 'in_home' ||
            appointmentItem.appointment?.details?.walking?.type === 'in_home',
        address:
            appointmentItem.appointment.details?.transportation?.address ||
            appointmentItem.appointment.details?.walking?.address ||
            appointmentItem.appointment.details?.training?.address,
        instructions:
            appointmentItem.appointment.details?.transportation?.instructions ||
            appointmentItem.appointment.details?.walking?.instructions ||
            appointmentItem.appointment.details?.training?.instructions,
        serviceSessionId: appointmentItem.appointment.serviceSessionId,
        vehicle,
        notes: appointmentItem.appointment.details?.boarding?.notes,
        selectedOffer: (
            appointmentItem.appointment.details as { boarding?: { selectedOffer?: BoardingOffer } } | undefined
        )?.boarding?.selectedOffer
    };
};

export const getPetBusinessAreas = (order: Order, petId?: number): Array<string> | undefined => {
    if (petId == null) {
        return undefined;
    }

    const areas = order.items.appointments
        .filter(item => item.appointment.pet.id === petId)
        .map(item => item.appointment.services[0]?.businessArea?.id)
        .filter((area): area is string => Boolean(area));

    return Array.from(new Set(areas));
};

const getComplementaryItemProps = (
    order: Order,
    orderItemId: number
): {
    type?: itemTypes;
    parentId: number;
    petId?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    product?: Product;
    addOn?: AddOnService;
    custom?: string;
    discount?: MultipleBookingDiscount;
    quantity?: number;
    amount?: number;
    taxId?: number;
    staffId?: number;
    itemsUsed?: Array<number>;
    dates?: Array<AddOnDate>;
    primaryServiceStaffId?: number;
    petBusinessAreas?: Array<string>;
} => {
    const addOnServiceItem = order.items.addOnServices.find(item => item.orderItem.id === orderItemId);
    const productItem = order.items.products.find(item => item.orderItem.id === orderItemId);
    const customItem = order.items.customs.find(item => item.orderItem.id === orderItemId);

    const getPrimaryServiceStaffId = (petId: number) =>
        order.items.appointments.find(item => item.appointment.pet.id === petId)?.orderItem?.staffId;

    if (addOnServiceItem) {
        return {
            parentId: addOnServiceItem.pet?.clientId ?? order.client.id!,
            petId: addOnServiceItem.orderItem.petId,
            type: itemTypes.addOn,
            date: addOnServiceItem.orderItem.startedAt?.clone(),
            product: undefined,
            addOn: addOnServiceItem.addOnService,
            custom: undefined,
            staffId: addOnServiceItem.orderItem.staffId,
            quantity: addOnServiceItem.orderItem.quantity,
            amount: Number(addOnServiceItem.orderItem.amount),
            discount: convertDiscount(addOnServiceItem.orderItem.discount),
            primaryServiceStaffId:
                addOnServiceItem.orderItem.petId != null
                    ? getPrimaryServiceStaffId(addOnServiceItem.orderItem.petId)
                    : undefined,
            petBusinessAreas: getPetBusinessAreas(order, addOnServiceItem.orderItem.petId)
        };
    } else if (productItem) {
        return {
            parentId: productItem.pet?.clientId ?? order.client.id!,
            petId: productItem.orderItem.petId,
            type: itemTypes.product,
            date: productItem.orderItem.startedAt?.clone(),
            product: productItem.product,
            addOn: undefined,
            custom: undefined,
            staffId: productItem.orderItem.staffId,
            quantity: productItem.orderItem.quantity,
            amount: Number(productItem.orderItem.amount),
            discount: convertDiscount(productItem.orderItem.discount),
            itemsUsed: order.items.products
                .filter(item => item.orderItem.petId === productItem.orderItem.petId)
                .map(item => item.orderItem.orderizable.id!)
                .filter(id => id !== productItem.product.id),
            primaryServiceStaffId:
                productItem.orderItem.petId != null ? getPrimaryServiceStaffId(productItem.orderItem.petId) : undefined
        };
    } else if (customItem) {
        return {
            parentId: customItem.pet?.clientId ?? order.client.id!,
            petId: customItem.orderItem.petId,
            type: itemTypes.custom,
            date: customItem.orderItem.startedAt?.clone(),
            product: undefined,
            addOn: undefined,
            custom: customItem.orderItem.description,
            staffId: customItem.orderItem.staffId,
            quantity: customItem.orderItem.quantity,
            amount: Number(customItem.orderItem.amount),
            taxId: customItem.orderItem.taxId,
            discount: convertDiscount(customItem.orderItem.discount),
            itemsUsed: undefined,
            primaryServiceStaffId:
                customItem.orderItem.petId != null ? getPrimaryServiceStaffId(customItem.orderItem.petId) : undefined
        };
    } else {
        return {
            parentId: order.client.id!
        };
    }
};

export const getOrderItemDescription = (
    addOnService?: AddOnService,
    product?: Product,
    custom?: string
): string | undefined => {
    if (addOnService) {
        return addOnService.name;
    } else if (product) {
        return product.businessProduct?.name ?? product.name;
    } else {
        return custom;
    }
};

export const getOrderItemDiscount = (discount?: MultipleBookingDiscount): Discount | undefined => {
    if (discount === undefined || discount.type === 'none') {
        return undefined;
    }

    if (!discount.value) {
        return { amount: '0' };
    }

    return {
        amount: discount.type === 'amount' ? discount.value?.toString() : undefined,
        percentage: discount.type === 'percentage' ? discount.value : undefined
    };
};

const toOrderItemDiscount = (payload?: { amount?: string; percentage?: number }): Discount | undefined =>
    payload == null || (payload.amount == null && payload.percentage == null)
        ? undefined
        : { amount: payload.amount, percentage: payload.percentage };

export const getOrderItemOrderizable = (
    addOnService?: AddOnService,
    product?: Product,
    custom?: string
): Orderizable | undefined => {
    if (addOnService) {
        return {
            type: OrderizableTypes.ADDON_SERVICE,
            id: addOnService.id!
        };
    } else if (product) {
        return {
            type: OrderizableTypes.PRODUCT,
            id: product.businessProduct?.id ?? product.id
        };
    } else if (custom) {
        return {
            type: OrderizableTypes.CUSTOM
        };
    } else {
        return undefined;
    }
};

export const getUsedPetServices = (order: Order, petId: number, serviceId?: number) =>
    uniq(
        order.items.appointments
            .filter(
                item =>
                    item.appointment.pet.id === petId &&
                    (serviceId === undefined || item.appointment.services[0].id !== serviceId)
            )
            .map(item => item.appointment.services[0].id)
    );

export const getUsedPetIds = (order: Order) => uniq(order.items.appointments.map(item => item.appointment.pet.id));

const getItemsUsed = (order: Order, orderItemId: number) => {
    const addOnServiceItem = order.items.addOnServices.find(item => item.orderItem.id === orderItemId);
    return order.items.addOnServices
        .filter(item => item.orderItem.petId === addOnServiceItem?.orderItem.petId)
        .map(item => item.orderItem.orderizable.id!);
};

const getDates = (order: Order, orderItemId: number) => {
    const addOnServiceItem = order.items.addOnServices.find(item => item.orderItem.id === orderItemId);

    return order.items.addOnServices
        .filter(item => item.orderItem.petId === addOnServiceItem?.orderItem.petId)
        .filter(item => item.orderItem.orderizable.id === addOnServiceItem?.orderItem.orderizable.id)
        .map(item => ({
            id: item.orderItem.orderizable.id!,
            date: item.orderItem.startedAt?.format('L') ?? ''
        }));
};

export interface SingleRescheduleInformation {
    bookingId: number;
    appointmentId: number;
    date: Moment;
    staffId: number | undefined | null;
    serviceId: number | undefined;
    notes: string | undefined;
    sendNotification: boolean | undefined;
    orderItemPrice: number | undefined;
    dateEnd: Moment | undefined;
    discount: NewBookingDiscount | undefined;
    roomUnitId: number | undefined | null;
    tasks: Task[] | undefined;
    serviceSessionId?: number | null;
    details:
        | {
              training?:
                  | {
                        type: 'in_home' | 'brick_and_mortar';
                        address: string;
                        duration?: number;
                        instructions?: string;
                        sessionType?: SessionBookingType;
                    }
                  | undefined;
              walking?:
                  | {
                        type: 'in_home' | 'brick_and_mortar';
                        address: string;
                        duration?: number;
                        instructions?: string;
                    }
                  | undefined;
              boarding?:
                  | {
                        room?: number;
                        notes?: string;
                        selectedOffer?: BoardingOffer;
                    }
                  | undefined;
              transportation?:
                  | {
                        type: 'pickup' | 'dropoff';
                        vehicleId: number;
                        address?: string;
                        instructions?: string;
                    }
                  | undefined;
          }
        | undefined;
}

interface RecurrentRescheduleInformation {
    appointmentId: number;
    date: Moment;
    staffId: number | undefined | null;
    serviceId: number | undefined;
    frecuencyId: string | undefined;
    notes: string | undefined;
    sendNotification: boolean | undefined;
    orderItemPrice: number | undefined;
    dateEnd: Moment | undefined;
    discount: NewBookingDiscount | undefined;
}

export const EditOrderItem: FunctionComponent<EditOrderItemProps> = props => {
    console.info({ order: props.order });
    console.info({ orderItemId: props.orderItemId });

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const boardingServices = useSelector<RootState, Array<BoardingService>>(
        state => state.services.boardingServices || []
    );
    const vehicles = useSelector<RootState, Array<Vehicle>>(state => state.vehicles.vehicles || []);

    const { morning, afternoon, evening } = getFeedingSlotTimes(marketplace, masterData);

    const appointmentItem = useMemo(
        () => props.order.items.appointments.find(item => item.orderItem.id === props.orderItemId),
        [props.order, props.orderItemId]
    );

    const transportationVehicle = useMemo(() => {
        const vehicleId = appointmentItem?.appointment.details?.transportation?.vehicleId;
        if (vehicleId == null) return undefined;
        return vehicles.find(vehicle => vehicle.id === vehicleId);
    }, [appointmentItem, vehicles]);

    const transportationType = useMemo<TransportationType | undefined>(() => {
        const type = appointmentItem?.appointment.details?.transportation?.type;
        return type === 'pickup' || type === 'dropoff' ? type : undefined;
    }, [appointmentItem]);

    const businessAreaId = appointmentItem?.appointment.services[0]?.businessArea?.id;

    const hasASingleAppoinment = useMemo(
        () =>
            props.order.items.appointments.length +
                props.order.items.addOnServices.length +
                props.order.items.products.length +
                props.order.items.customs.length ===
            1,
        [props.order]
    );

    const inRecurringSeries = appointmentItem?.appointment.recurringSeriesId != null;

    const isNotLastRecurrentAppointment = useMemo(() => {
        const primaryServiceItem = props.order.items.appointments[0];
        return (
            !inRecurringSeries &&
            primaryServiceItem.orderItem.id === props.orderItemId &&
            primaryServiceItem.appointment.recurringUuid &&
            primaryServiceItem.appointment.duration.from.clone().isBefore(props.recurrentInfo?.lastDate)
        );
    }, [props.order, props.orderItemId]);

    const isEvaluation = useMemo(() => {
        return appointmentItem?.appointment?.services?.[0].businessArea.id === 'evaluation';
    }, [appointmentItem]);

    const isDental = useMemo(() => {
        return appointmentItem?.appointment?.services?.[0].businessArea.id === 'dental';
    }, [appointmentItem]);

    const [saving, setSaving] = useState(false);

    const [recurrentRescheduleConfirmation, setRecurrentRescheduleConfirmation] = useState<{
        show: boolean;
        recurrentRescheduleInfo?: RecurrentRescheduleInformation;
        singleRescheduleInfo?: SingleRescheduleInformation;
        upcomingAppoinmentsCount?: number;
    }>({ show: false });

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderItemSuccess:
                setSaving(false);
                props.onSaveSuccess?.();
                props.onClose();
                break;
            case OrdersStatus.ReloadSuccess:
                setSaving(false);
                props.onSaveSuccess?.();
                props.onClose();
                break;
            case OrdersStatus.NotFound:
            case OrdersStatus.Error:
            case OrdersStatus.SaveOrderScopeRequired:
            case OrdersStatus.SaveOrderScopeCancelled:
                setSaving(false);
                closeRecurrentRescheduleConfirmation();
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduleSuccess:
            case NewBookingsStatus.SingleMultipleRescheduleSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            case NewBookingsStatus.RecurrentRescheduleSuccess:
                props.onCloseOrderDetail && props.onCloseOrderDetail();
                break;
            case NewBookingsStatus.UpdateAppointmentScopeRequired:
            case NewBookingsStatus.UpdateAppointmentScopeCancelled:
                setSaving(false);
                break;
            case NewBookingsStatus.NotFound:
            case NewBookingsStatus.Error:
                closeRecurrentRescheduleConfirmation();
                setSaving(false);
        }
    }, [newBookingsStatus]);

    const usedPetServicesIds: Array<number> = useMemo(
        () => getUsedPetServices(props.order, appointmentItem?.pet.id!, appointmentItem?.appointment.services[0].id),
        [props.order, props.orderItemId]
    );

    const usedPetIds: Array<number> = useMemo(() => getUsedPetIds(props.order), [props.order, props.orderItemId]);

    const complementaryItemProps = useMemo(
        () => getComplementaryItemProps(props.order, props.orderItemId),
        [props.order, props.orderItemId]
    );

    const rescheduleSingleAppointment = (singleRescheduleInfo: SingleRescheduleInformation) => {
        setSaving(true);

        if (inRecurringSeries) {
            dispatch(
                updateAppointmentThunk(
                    apiClientWrapper,
                    singleRescheduleInfo.appointmentId,
                    {
                        type: 'update',
                        appointment: {
                            bookedAt: singleRescheduleInfo.date,
                            bookingEndAt: singleRescheduleInfo.dateEnd,
                            staffId: singleRescheduleInfo.staffId,
                            serviceId: singleRescheduleInfo.serviceId,
                            notes: singleRescheduleInfo.notes,
                            orderItemPrice: singleRescheduleInfo.orderItemPrice,
                            discount: singleRescheduleInfo.discount,
                            removeEnrollment: true,
                            roomUnitId: singleRescheduleInfo.roomUnitId,
                            tasks: singleRescheduleInfo.tasks,
                            details: singleRescheduleInfo.details
                        }
                    },
                    singleRescheduleInfo.sendNotification,
                    'order_detail',
                    INITIAL_BOOKING_EDIT_SCOPE_REQUEST
                )
            );
            return;
        }

        dispatch(
            singleRescheduleThunk(
                apiClientWrapper,
                singleRescheduleInfo.bookingId,
                singleRescheduleInfo.appointmentId,
                singleRescheduleInfo.date,
                singleRescheduleInfo.staffId,
                singleRescheduleInfo.serviceId,
                singleRescheduleInfo.notes,
                singleRescheduleInfo.sendNotification,
                singleRescheduleInfo.orderItemPrice,
                singleRescheduleInfo.dateEnd,
                singleRescheduleInfo.discount,
                true,
                singleRescheduleInfo.roomUnitId,
                singleRescheduleInfo.tasks,
                singleRescheduleInfo.details,
                undefined,
                undefined,
                singleRescheduleInfo.serviceSessionId
            )
        );
    };

    const rescheduleRecurrentAppointment = (recurrentRescheduleInfo: RecurrentRescheduleInformation) => {
        setSaving(true);

        dispatch(
            recurrentRescheduleThunk(
                apiClientWrapper,
                recurrentRescheduleInfo.appointmentId,
                recurrentRescheduleInfo.date,
                recurrentRescheduleInfo.staffId,
                recurrentRescheduleInfo.serviceId,
                recurrentRescheduleInfo.frecuencyId,
                recurrentRescheduleInfo.notes,
                recurrentRescheduleInfo.sendNotification,
                recurrentRescheduleInfo.orderItemPrice,
                recurrentRescheduleInfo.dateEnd,
                recurrentRescheduleInfo.discount
            )
        );
    };

    const closeRecurrentRescheduleConfirmation = () => setRecurrentRescheduleConfirmation({ show: false });

    const saveServiceHandler = (
        date: Moment | undefined,
        petId: number | undefined,
        service: PetServiceWrapper | undefined,
        staffId: number | undefined,
        discount: MultipleBookingDiscount | undefined
    ) => {
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromPetService(service));
        const appointment = appointmentItem?.appointment;

        if (appointment) {
            const nextDate = date?.clone() ?? appointment.duration.from.clone();
            const nextServiceId = service?.service.id ?? appointment.services[0]?.id;
            const membershipDiscount = toOrderItemDiscount(resolveDiscountPayloadForSave(discount, service));

            const singleRescheduleInfo: SingleRescheduleInformation = {
                bookingId: appointment.bookingId,
                appointmentId: appointment.id!,
                date: nextDate,
                staffId: staffId,
                serviceId: nextServiceId,
                notes: undefined,
                sendNotification: false,
                orderItemPrice: resolveOrderItemPriceForSave(service),
                dateEnd: nextDate.clone().add(service?.durationInMinutes ?? 0, 'minutes'),
                discount: membershipDiscount,
                roomUnitId: undefined,
                tasks: undefined,
                details: undefined
            };

            if (isNotLastRecurrentAppointment && hasASingleAppoinment) {
                const recurrentRescheduleInfo: RecurrentRescheduleInformation = {
                    appointmentId: appointment.id!,
                    date: date?.clone() ?? appointment.duration.from.clone(),
                    staffId: staffId,
                    serviceId: service?.service.id,
                    frecuencyId: appointment.recurringFrecuency?.id,
                    notes: undefined,
                    sendNotification: false,
                    orderItemPrice: resolveOrderItemPriceForSave(service),
                    dateEnd: date?.clone().add(service?.durationInMinutes, 'minutes'),
                    discount: membershipDiscount
                };

                setRecurrentRescheduleConfirmation({
                    show: true,
                    upcomingAppoinmentsCount: props.recurrentInfo?.remaining
                        ? props.recurrentInfo?.remaining - 1
                        : undefined,
                    recurrentRescheduleInfo,
                    singleRescheduleInfo
                });
            } else if (appointment.duration.from.isSame(nextDate, 'day')) {
                rescheduleSingleAppointment(singleRescheduleInfo);
            } else {
                const appointmentItemToRebook = cloneDeep(appointmentItem);

                if (date) {
                    appointmentItemToRebook.appointment.duration.from = date.clone();
                    appointmentItemToRebook.appointment.duration.to = service
                        ? date.clone().add(service.durationInMinutes, 'minutes')
                        : appointmentItemToRebook.appointment.duration.to;
                }
                if (service) {
                    appointmentItemToRebook.appointment.services[0].id = service.service.id;
                    appointmentItemToRebook.appointment.services[0].name = service.service.name;
                    appointmentItemToRebook.orderItem.amount = service.price.toFixed(2);
                }

                if (staffId) {
                    appointmentItemToRebook.orderItem.staffId = staffId;
                }

                if (discount) {
                    appointmentItemToRebook.orderItem.discount = getOrderItemDiscount(discount);
                }

                rescheduleSingleAppointment({
                    ...singleRescheduleInfo,
                    sendNotification: false
                });
            }
        }
    };

    const saveDaycareHandler = (
        daycare: Daycare,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromDaycare(daycare));

        const feedingSlots = { morning, afternoon, evening };
        const originalFeedings = wrapService(appointmentItem!, timezone, feedingSlots)?.feedings || [];
        const currentFeedingTaskIds = new Set<number>();
        feedings.forEach(feeding => {
            if (feedingIsSpecificTimeOnSave(feeding)) {
                getSpecificFeedingMomentIds(feeding).forEach(momentType => {
                    const id = feeding.momentTypeToTaskIdMap?.get(momentType)?.[0];
                    if (id) currentFeedingTaskIds.add(id);
                });
            } else if (feeding.momentTypes?.length) {
                feeding.momentTypes.forEach(momentType =>
                    currentFeedingTaskIds.add(feeding.momentTypeToTaskIdMap?.get(momentType)?.[0] ?? 0)
                );
            }
        });

        const deletedFeedings: Task[] = [];
        originalFeedings.forEach(originalFeeding => {
            if (originalFeeding.taskIds) {
                originalFeeding.taskIds.forEach(taskId => {
                    if (!currentFeedingTaskIds.has(taskId)) {
                        deletedFeedings.push({
                            id: taskId,
                            feeding: { taskType: 1 as const },
                            destroy: true
                        });
                    }
                });
            }
        });

        const originalMedications = wrapService(appointmentItem!, timezone, feedingSlots)?.medications || [];

        const currentMedicationTaskIds = new Set<number>();
        medications.forEach(medication => {
            if (medication.schedule && medication.schedule.length > 0) {
                medication.schedule.forEach(taskId =>
                    currentMedicationTaskIds.add(medication.scheduleToTaskIdMap?.get(taskId)?.[0] ?? 0)
                );
            }

            if (medication.specificSchedule && medication.specificSchedule.length > 0) {
                medication.specificSchedule.forEach(daySchedule => {
                    if (daySchedule.enabled && daySchedule.doses && daySchedule.doses.length > 0) {
                        daySchedule.doses.forEach(scheduleTime => {
                            const mappedTaskIds = medication.scheduleToTaskIdMap?.get(scheduleTime) ?? [];
                            mappedTaskIds.forEach(mappedTaskId => {
                                if (mappedTaskId !== undefined) {
                                    currentMedicationTaskIds.add(mappedTaskId);
                                }
                            });
                        });
                    }
                });
            }
        });

        const deletedMedications: Task[] = [];
        originalMedications.forEach(originalMedication => {
            if (originalMedication.taskIds) {
                originalMedication.taskIds.forEach(taskId => {
                    if (!currentMedicationTaskIds.has(taskId)) {
                        deletedMedications.push({
                            id: taskId,
                            medication: { taskType: 2 as const },
                            destroy: true
                        });
                    }
                });
            }
        });

        const feedingTasks: Task[] = feedings.flatMap(feeding => {
            if (feedingIsSpecificTimeOnSave(feeding)) {
                return getSpecificFeedingMomentIds(feeding).flatMap(momentTypeId => {
                    const timeString = feeding.specificMomentTimes?.[momentTypeId];
                    if (!timeString?.trim()) return [];
                    const taskId = feeding.momentTypeToTaskIdMap?.get(momentTypeId)?.[0];
                    const taskDate = createMedicationTaskDate(
                        daycare.from.clone().tz(timezone),
                        String(timeString).trim(),
                        timezone
                    );
                    return [
                        {
                            id: taskId,
                            taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId,
                                specificTime: true as const
                            }
                        }
                    ];
                });
            }
            return (
                feeding?.momentTypes?.map(momentTypeId => {
                    const taskId = feeding.momentTypeToTaskIdMap?.get(momentTypeId)?.[0];

                    const taskDate = createTaskDate(
                        daycare.from,
                        momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                        timezone
                    );

                    return {
                        id: taskId,
                        taskDate: taskDate,
                        feeding: {
                            taskType: 1 as const,
                            feedingMomentTypeId: momentTypeId,
                            foodProvisionerTypeId: feeding.foodProvisionType,
                            feedingBrand: feeding.brand,
                            foodTypeId: feeding.foodType,
                            foodQuantity: feeding.quantity.quantity?.toString(),
                            foodQuantityUnitId: feeding.quantity.unit,
                            feedingInstructions: feeding.instructions,
                            roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                        }
                    };
                }) || []
            );
        });

        const medicationTasks: Task[] = medications.flatMap(medication => {
            if (medication.schedule && medication.schedule.length > 0) {
                return medication.schedule.map(scheduleTime => {
                    const taskId = medication.scheduleToTaskIdMap?.get(scheduleTime)?.[0];
                    const taskDate = createMedicationTaskDate(daycare.from, scheduleTime, timezone);

                    return {
                        id: taskId,
                        taskDate: taskDate,
                        medication: {
                            taskType: 2 as const,
                            medicationName: medication.name,
                            medicationMethodId: medication.methodType,
                            dosageAmount: medication.dosage.quantity?.toString(),
                            dosageUnitId: medication.dosage.unit,
                            medicationInstructions: medication.instructions,
                            roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                        }
                    };
                });
            }

            if (medication.specificSchedule && medication.specificSchedule.length > 0) {
                const tasks = medication.specificSchedule.flatMap(daySchedule => {
                    if (!daySchedule.enabled || !daySchedule.doses || daySchedule.doses.length === 0) {
                        return [];
                    }

                    return daySchedule.doses.map(scheduleTime => {
                        const availableTaskIds = medication.scheduleToTaskIdMap?.get(scheduleTime) ?? [];
                        const usedTaskIds = new Set(
                            medicationTasks.map(task => task.id).filter(id => id !== undefined)
                        );
                        const taskId = availableTaskIds.find(id => !usedTaskIds.has(id));

                        const taskDate = createMedicationTaskDate(daySchedule.date, scheduleTime, timezone);

                        if (taskId !== undefined) {
                            currentMedicationTaskIds.add(taskId);
                        }

                        return {
                            id: taskId,
                            taskDate: taskDate,
                            medication: {
                                taskType: 2 as const,
                                medicationName: medication.name,
                                medicationMethodId: medication.methodType,
                                dosageAmount: medication.dosage.quantity?.toString(),
                                dosageUnitId: medication.dosage.unit,
                                medicationInstructions: medication.instructions,
                                roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                            }
                        };
                    });
                });

                return tasks;
            }

            return [];
        });

        const tasks: Task[] = [...feedingTasks, ...medicationTasks, ...deletedFeedings, ...deletedMedications];

        const daycarePricing = resolveDaycareSavePricing(daycare);
        const singleRescheduleInfo: SingleRescheduleInformation = {
            bookingId: appointmentItem?.appointment.bookingId!,
            appointmentId: appointmentItem?.appointment.id!,
            date: daycare.from.clone(),
            staffId: undefined,
            serviceId: daycare.service?.serviceId!,
            notes: undefined,
            sendNotification: false,
            orderItemPrice: daycarePricing.orderItemPrice,
            dateEnd: daycare.to?.clone() || daycare.from.clone().add(8, 'hours'),
            discount:
                daycarePricing.discountAmount != null || daycarePricing.discountPercentage != null
                    ? toOrderItemDiscount({
                          amount: daycarePricing.discountAmount,
                          percentage: daycarePricing.discountPercentage
                      })
                    : undefined,
            roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId,
            tasks: tasks,
            details: undefined
        };

        rescheduleSingleAppointment(singleRescheduleInfo);
    };

    const getRangeDateByMomentTypeId = (date: Moment, momentTypeId: number) => {
        let timeStart = '';
        let timeEnd = '';
        if (momentTypeId === 1) {
            timeStart = morning;
            timeEnd = afternoon;
        } else if (momentTypeId === 2) {
            timeStart = afternoon;
            timeEnd = evening;
        } else {
            timeStart = evening;
            timeEnd = '2359';
        }

        const paddedTimeStart = timeStart.padStart(4, '0');
        const paddedTimeEnd = timeEnd.padStart(4, '0');

        const hourStart = parseInt(paddedTimeStart.substring(0, 2), 10);
        const minuteStart = parseInt(paddedTimeStart.substring(2, 4), 10);

        const hourEnd = parseInt(paddedTimeEnd.substring(0, 2), 10);
        const minuteEnd = parseInt(paddedTimeEnd.substring(2, 4), 10);

        const selectedHourStart = `${hourStart.toString().padStart(2, '0')}:${minuteStart
            .toString()
            .padStart(2, '0')} ${hourStart >= 12 ? 'PM' : 'AM'}`;

        const selectedHourEnd = `${hourEnd.toString().padStart(2, '0')}:${minuteEnd.toString().padStart(2, '0')} ${
            hourEnd >= 12 ? 'PM' : 'AM'
        }`;

        return {
            from: moment.tz(
                `${date.format('YYYY-MM-DD')} ${selectedHourStart.toUpperCase()}`,
                'YYYY-MM-DD hh:mm A',
                timezone
            ),
            to: moment.tz(
                `${date.format('YYYY-MM-DD')} ${selectedHourEnd.toUpperCase()}`,
                'YYYY-MM-DD hh:mm A',
                timezone
            )
        };
    };

    const saveBoardingHandler = (
        boarding: Boarding,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromBoarding(boarding));

        const feedingSlotsBoarding = { morning, afternoon, evening };
        const originalFeedings = wrapService(appointmentItem!, timezone, feedingSlotsBoarding)?.feedings || [];
        const currentFeedingTaskIds = new Set<number>();
        const daysDifference = boarding.to ? boarding.to.clone().diff(boarding.from.clone(), 'days') + 1 : 1;
        const endDateExclusive = boarding.to ? boarding.to.clone() : boarding.from.clone().add(1, 'days');
        feedings.forEach(feeding => {
            if (feedingIsSpecificTimeOnSave(feeding)) {
                for (let dayOffset = 0; dayOffset < daysDifference; dayOffset++) {
                    getSpecificFeedingMomentIds(feeding).forEach(momentType => {
                        const taskIds = feeding.momentTypeToTaskIdMap?.get(momentType) ?? [];
                        const idToUse = uniq(taskIds)[dayOffset];
                        if (idToUse !== undefined) currentFeedingTaskIds.add(idToUse);
                    });
                }
            } else if (feeding.momentTypes) {
                feeding.momentTypes.forEach(momentType => {
                    for (let dayOffset = 0; dayOffset < daysDifference; dayOffset++) {
                        const currentDay = boarding.from.clone().add(dayOffset, 'days');
                        const rangeDate = getRangeDateByMomentTypeId(currentDay, momentType);

                        const hasIntersection =
                            (boarding.from.isSameOrAfter(rangeDate.from) && boarding.from.isBefore(rangeDate.to)) ||
                            (endDateExclusive.isAfter(rangeDate.from) && endDateExclusive.isBefore(rangeDate.to)) ||
                            (rangeDate.from.isSameOrAfter(boarding.from) &&
                                rangeDate.from.isBefore(endDateExclusive)) ||
                            (rangeDate.to.isAfter(boarding.from) && rangeDate.to.isBefore(endDateExclusive)) ||
                            (boarding.from.isSameOrBefore(rangeDate.from) && endDateExclusive.isAfter(rangeDate.to));

                        if (!hasIntersection) {
                            return;
                        }

                        const taskIds = feeding.momentTypeToTaskIdMap?.get(momentType) ?? [];
                        const uniqueTaskIds = uniq(taskIds);
                        const idToUse = uniqueTaskIds[dayOffset];
                        if (idToUse !== undefined) {
                            currentFeedingTaskIds.add(idToUse);
                        }
                    }
                });
            }
        });

        const deletedFeedings: Task[] = [];
        originalFeedings.forEach(originalFeeding => {
            if (originalFeeding.taskIds) {
                originalFeeding.taskIds.forEach(taskId => {
                    if (!currentFeedingTaskIds.has(taskId)) {
                        deletedFeedings.push({
                            id: taskId,
                            feeding: { taskType: 1 as const },
                            destroy: true
                        });
                    }
                });
            }
        });

        const originalMedications = wrapService(appointmentItem!, timezone, feedingSlotsBoarding)?.medications || [];

        const currentMedicationTaskIds = new Set<number>();
        medications.forEach(medication => {
            if (medication.schedule && medication.schedule.length > 0) {
                medication.schedule.forEach(taskId => {
                    const mappedTaskIds = medication.scheduleToTaskIdMap?.get(taskId) ?? [];
                    uniq(mappedTaskIds).forEach(mappedTaskId => {
                        if (mappedTaskId !== undefined) {
                            currentMedicationTaskIds.add(mappedTaskId);
                        }
                    });
                });
            }

            if (medication.specificSchedule && medication.specificSchedule.length > 0) {
                medication.specificSchedule.forEach(daySchedule => {
                    if (daySchedule.enabled && daySchedule.doses && daySchedule.doses.length > 0) {
                        daySchedule.doses.forEach(scheduleTime => {
                            const mappedTaskIds = medication.scheduleToTaskIdMap?.get(scheduleTime) ?? [];
                            uniq(mappedTaskIds).forEach(mappedTaskId => {
                                if (mappedTaskId !== undefined) {
                                    currentMedicationTaskIds.add(mappedTaskId);
                                }
                            });
                        });
                    }
                });
            }
        });

        const deletedMedications: Task[] = [];
        originalMedications.forEach(originalMedication => {
            if (originalMedication.taskIds) {
                originalMedication.taskIds.forEach(taskId => {
                    if (!currentMedicationTaskIds.has(taskId)) {
                        deletedMedications.push({
                            id: taskId,
                            medication: { taskType: 2 as const },
                            destroy: true
                        });
                    }
                });
            }
        });

        const feedingTasks: Task[] = [];
        for (let dayOffset = 0; dayOffset < daysDifference; dayOffset++) {
            const currentDay = boarding.from.clone().add(dayOffset, 'days');

            feedings.forEach(feeding => {
                if (feedingIsSpecificTimeOnSave(feeding)) {
                    getSpecificFeedingMomentIds(feeding).forEach(momentTypeId => {
                        const timeString = feeding.specificMomentTimes?.[momentTypeId];
                        if (!timeString?.trim()) return;
                        const availableTaskIds = feeding.momentTypeToTaskIdMap?.get(momentTypeId) ?? [];
                        const usedTaskIds = new Set(feedingTasks.map(task => task.id).filter(id => id !== undefined));
                        const taskId = availableTaskIds.find(id => !usedTaskIds.has(id));
                        const taskDate = createMedicationTaskDate(currentDay, String(timeString).trim(), timezone);
                        if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(endDateExclusive)) {
                            return;
                        }
                        feedingTasks.push({
                            id: taskId,
                            taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: boarding.roomUnitId,
                                specificTime: true
                            }
                        });
                    });
                } else {
                    const momentTypes = feeding?.momentTypes ?? [];
                    uniq(momentTypes).forEach(momentTypeId => {
                        const availableTaskIds = feeding.momentTypeToTaskIdMap?.get(momentTypeId) ?? [];
                        const usedTaskIds = new Set(feedingTasks.map(task => task.id).filter(id => id !== undefined));

                        const taskId = availableTaskIds.find(id => !usedTaskIds.has(id));

                        const taskDate = createTaskDate(
                            currentDay,
                            momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                            timezone
                        );

                        feedingTasks.push({
                            id: taskId,
                            taskDate: taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: boarding.roomUnitId
                            }
                        });
                    });
                }
            });
        }

        const medicationTasks: Task[] = [];

        medications.forEach(medication => {
            if (medication.schedule && medication.schedule.length > 0) {
                for (let dayOffset = 0; dayOffset < daysDifference; dayOffset++) {
                    const currentDay = boarding.from.clone().add(dayOffset, 'days');

                    const schedules = medication.schedule ?? [];
                    uniq(schedules).forEach(scheduleTime => {
                        const availableTaskIds = medication.scheduleToTaskIdMap?.get(scheduleTime) ?? [];
                        const usedTaskIds = new Set(
                            medicationTasks.map(task => task.id).filter(id => id !== undefined)
                        );

                        const taskId = availableTaskIds.find(id => !usedTaskIds.has(id));

                        const taskDate = createMedicationTaskDate(currentDay, scheduleTime, timezone);

                        if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(endDateExclusive)) {
                            return;
                        }

                        medicationTasks.push({
                            id: taskId,
                            taskDate: taskDate,
                            medication: {
                                taskType: 2 as const,
                                medicationName: medication.name,
                                medicationMethodId: medication.methodType,
                                dosageAmount: medication.dosage.quantity?.toString(),
                                dosageUnitId: medication.dosage.unit,
                                medicationInstructions: medication.instructions,
                                roomUnitId: boarding.roomUnitId
                            }
                        });
                    });
                }
            }

            if (medication.specificSchedule && medication.specificSchedule.length > 0) {
                medication.specificSchedule.forEach(daySchedule => {
                    if (!daySchedule.enabled || !daySchedule.doses || daySchedule.doses.length === 0) {
                        return;
                    }

                    daySchedule.doses.forEach(scheduleTime => {
                        const availableTaskIds = medication.scheduleToTaskIdMap?.get(scheduleTime) ?? [];
                        const usedTaskIds = new Set(
                            medicationTasks.map(task => task.id).filter(id => id !== undefined)
                        );

                        const taskId = availableTaskIds.find(id => !usedTaskIds.has(id));

                        const taskDate = createMedicationTaskDate(daySchedule.date, scheduleTime, timezone);

                        if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(endDateExclusive)) {
                            return;
                        }

                        medicationTasks.push({
                            id: taskId,
                            taskDate: taskDate,
                            medication: {
                                taskType: 2 as const,
                                medicationName: medication.name,
                                medicationMethodId: medication.methodType,
                                dosageAmount: medication.dosage.quantity?.toString(),
                                dosageUnitId: medication.dosage.unit,
                                medicationInstructions: medication.instructions,
                                roomUnitId: boarding.roomUnitId
                            }
                        });

                        if (taskId !== undefined) {
                            currentMedicationTaskIds.add(taskId);
                        }
                    });
                });
            }
        });

        const tasks: Task[] = [...feedingTasks, ...medicationTasks, ...deletedFeedings, ...deletedMedications];

        const boardingDateToSave = boarding.from.clone().seconds(0).milliseconds(0);
        const boardingDateEndToSave = boarding.to
            ? boarding.to.clone().seconds(0).milliseconds(0)
            : boardingDateToSave.clone().add(8, 'hours');

        const selectedBoardingService = boarding.service?.serviceId
            ? boardingServices.find(s => s.serviceId === boarding.service?.serviceId)
            : undefined;
        const roomForDetails = selectedBoardingService?.rooms?.[0];

        const singleRescheduleInfo: SingleRescheduleInformation = {
            bookingId: appointmentItem?.appointment.bookingId!,
            appointmentId: appointmentItem?.appointment.id!,
            date: boardingDateToSave,
            staffId: undefined,
            serviceId: boarding.service?.serviceId!,
            notes: undefined,
            sendNotification: false,
            orderItemPrice: resolveBoardingOrderItemPriceForSave(boarding),
            dateEnd: boardingDateEndToSave,
            discount: {
                amount: undefined,
                percentage: undefined
            },
            roomUnitId: boarding.roomUnitId ?? null,
            tasks: tasks,
            details: {
                boarding: {
                    room: roomForDetails?.id,
                    notes: boarding.notes,
                    selectedOffer: boarding.service?.selectedOffer
                }
            }
        };

        rescheduleSingleAppointment(singleRescheduleInfo);
    };

    const saveTrainingHandler = (training: Training, petId: number | undefined) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromTraining(training));
        const trainingDateToSave = training.from.clone().seconds(0).milliseconds(0);
        const durationMinutes =
            training.service?.duration && training.service.duration > 0
                ? training.service.duration
                : training.to && training.from
                ? Math.max(0, training.to.diff(training.from, 'minutes'))
                : 0;
        const trainingDateEndToSave = durationMinutes
            ? trainingDateToSave.clone().add(durationMinutes, 'minutes')
            : training.to
            ? training.to.clone().seconds(0).milliseconds(0)
            : trainingDateToSave.clone().add(8, 'hours');
        const trainingItemDiscount: MultipleBookingDiscount = {
            type: (training.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
            value: training.discount?.value ?? 0
        };
        const trainingPricing = resolveStandardServiceSavePricing({
            price: training.price,
            enrollment: training.enrollment,
            membershipItem: training.membershipItem,
            discount: trainingItemDiscount,
            businessArea: 'training'
        });
        const singleRescheduleInfo: SingleRescheduleInformation = {
            bookingId: appointmentItem?.appointment.bookingId!,
            appointmentId: appointmentItem?.appointment.id!,
            date: trainingDateToSave,
            staffId: training.staffId,
            serviceId: training.service?.serviceId!,
            notes: undefined,
            sendNotification: false,
            orderItemPrice: trainingPricing.orderItemPrice,
            dateEnd: trainingDateEndToSave,
            discount: toOrderItemDiscount({
                amount: trainingPricing.discountAmount,
                percentage: trainingPricing.discountPercentage
            }),
            details: {
                training: {
                    type: training.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                    address: training.service?.inHomeService ? training.address || '' : '',
                    duration: durationMinutes || training.service?.duration,
                    instructions: training.service?.inHomeService ? training.instructions : undefined,
                    sessionType: resolveBookingType(training.service?.bookingType)
                }
            },
            serviceSessionId: training.serviceSessionId ?? null,
            roomUnitId: undefined,
            tasks: undefined
        };

        rescheduleSingleAppointment(singleRescheduleInfo);
    };

    const saveWalkingHandler = (walking: Walking, petId: number | undefined) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromWalking(walking));
        const walkingDateToSave = walking.from.clone().seconds(0).milliseconds(0);
        const durationMinutes =
            walking.service?.duration && walking.service.duration > 0
                ? walking.service.duration
                : walking.to && walking.from
                ? Math.max(0, walking.to.diff(walking.from, 'minutes'))
                : 0;
        const walkingDateEndToSave = durationMinutes
            ? walkingDateToSave.clone().add(durationMinutes, 'minutes')
            : walking.to
            ? walking.to.clone().seconds(0).milliseconds(0)
            : walkingDateToSave.clone().add(8, 'hours');
        const walkingItemDiscount: MultipleBookingDiscount = {
            type: (walking.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
            value: walking.discount?.value ?? 0
        };
        const walkingPricing = resolveStandardServiceSavePricing({
            price: walking.price,
            enrollment: walking.enrollment,
            membershipItem: walking.membershipItem,
            discount: walkingItemDiscount,
            businessArea: 'walking'
        });
        const singleRescheduleInfo: SingleRescheduleInformation = {
            bookingId: appointmentItem?.appointment.bookingId!,
            appointmentId: appointmentItem?.appointment.id!,
            date: walkingDateToSave,
            staffId: walking.staffId,
            serviceId: walking.service?.serviceId!,
            notes: undefined,
            sendNotification: false,
            orderItemPrice: walkingPricing.orderItemPrice,
            dateEnd: walkingDateEndToSave,
            discount: toOrderItemDiscount({
                amount: walkingPricing.discountAmount,
                percentage: walkingPricing.discountPercentage
            }),
            details: {
                walking: {
                    type: walking.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                    address: walking.service?.inHomeService ? walking.address || '' : '',
                    duration: durationMinutes || walking.service?.duration,
                    instructions: walking.service?.inHomeService ? walking.instructions : undefined
                }
            },
            serviceSessionId: walking.serviceSessionId ?? null,
            roomUnitId: undefined,
            tasks: undefined
        };

        rescheduleSingleAppointment(singleRescheduleInfo);
    };

    const saveTransportationHandler = (transportation: Transportation, _petId: number | undefined) => {
        if (!appointmentItem?.appointment) return;

        setSaving(true);
        persistOrderItemCustomPrices(
            dispatch,
            apiClientWrapper,
            _petId ?? appointmentItem.appointment.pet.id,
            customPriceDataFromTransportation(
                transportation,
                transportation.pickup?.time?.from ||
                    transportation.dropOff?.time?.from ||
                    appointmentItem.appointment.duration.from,
                timezone
            )
        );

        const originalType = appointmentItem.appointment.details?.transportation?.type;
        const effectiveType: 'pickup' | 'dropoff' =
            transportation.transportType === 'roundtrip'
                ? originalType === 'pickup'
                    ? 'pickup'
                    : 'dropoff'
                : transportation.transportType === 'pickup'
                ? 'pickup'
                : 'dropoff';

        const transportationData = effectiveType === 'pickup' ? transportation.pickup : transportation.dropOff;

        const dateToSave = (transportationData?.time.from.clone() ?? appointmentItem.appointment.duration.from.clone())
            .seconds(0)
            .milliseconds(0);
        const dateEndToSave = transportationData?.time.to
            ? transportationData.time.to.clone().seconds(0).milliseconds(0)
            : dateToSave.clone().add(transportation.service?.duration ?? 0, 'minutes');

        const transportationItemDiscount: MultipleBookingDiscount | undefined = transportationData?.discount
            ? {
                  type: (transportationData.discount.type === 'percentage' ? 'percentage' : 'amount') as
                      | 'amount'
                      | 'percentage',
                  value: transportationData.discount.value ?? 0
              }
            : undefined;
        const transportationPricing = resolveStandardServiceSavePricing({
            price: transportationData?.price || 0,
            enrollment: transportation.enrollment,
            membershipItem: transportation.membershipItem,
            discount: transportationItemDiscount,
            businessArea: 'transportation'
        });
        const singleRescheduleInfo: SingleRescheduleInformation = {
            bookingId: appointmentItem.appointment.bookingId!,
            appointmentId: appointmentItem.appointment.id!,
            date: dateToSave,
            staffId: transportation.selectedStaff?.id,
            serviceId: transportation.service?.serviceId!,
            notes: undefined,
            sendNotification: false,
            orderItemPrice: transportationPricing.orderItemPrice,
            dateEnd: dateEndToSave,
            discount: toOrderItemDiscount({
                amount: transportationPricing.discountAmount,
                percentage: transportationPricing.discountPercentage
            }),
            details: {
                transportation: {
                    type: effectiveType,
                    vehicleId: transportation.vehicle?.id || 0,
                    address: transportationData?.address,
                    instructions: transportationData?.instructions
                }
            },
            roomUnitId: undefined,
            tasks: undefined
        };

        rescheduleSingleAppointment(singleRescheduleInfo);
    };

    const saveOrderItemHandler = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: MultipleBookingDiscount,
        quantity?: number,
        amount?: number,
        taxId?: number
    ) => {
        setSaving(true);

        const orderItem = [
            ...props.order.items.addOnServices.map(item => item.orderItem),
            ...props.order.items.products.map(item => item.orderItem),
            ...props.order.items.customs.map(item => item.orderItem)
        ].find(orderItem => orderItem.id === props.orderItemId);

        if (orderItem) {
            const updatedOrderItem = {
                id: orderItem.id,
                uuid: orderItem.uuid,
                orderId: orderItem.orderId,
                orderizable: getOrderItemOrderizable(selectedAddOn, selectedProduct, custom) ?? {
                    ...orderItem.orderizable
                },
                petId: petId,
                staffId: staffId,
                description: getOrderItemDescription(selectedAddOn, selectedProduct, custom) ?? '',
                quantity: quantity ?? orderItem.quantity,
                amount: amount?.toString() ?? orderItem.amount,
                taxId: taxId,
                discount: getOrderItemDiscount(discount),
                startedAt: date?.clone()
            };

            if (isOrderInRecurringSeries(props.order)) {
                dispatch(
                    updateOrderItemScopedThunk(apiClientWrapper, updatedOrderItem, INITIAL_ORDER_EDIT_SCOPE_REQUEST)
                );
            } else {
                dispatch(saveOrderItemThunk(apiClientWrapper, updatedOrderItem));
            }
        }
    };

    return (
        <>
            {appointmentItem ? (
                <PetAndServices
                    parentId={appointmentItem?.appointment.customer.id}
                    petId={appointmentItem?.appointment.pet.id}
                    appointmentId={appointmentItem?.appointment.id}
                    service={wrapService(
                        appointmentItem,
                        timezone,
                        { morning, afternoon, evening },
                        transportationVehicle
                    )}
                    staffId={appointmentItem.appointment.services[0].staff?.id}
                    date={appointmentItem.appointment.duration.from.clone()}
                    dateEnd={appointmentItem.appointment.duration.to.clone()}
                    discount={
                        appointmentItem.orderItem.redeem?.type === RedeemTypes.ENROLLMENT &&
                        (appointmentItem.orderItem.discount?.amount || appointmentItem.orderItem.discount?.percentage)
                            ? convertDiscount(undefined)
                            : convertDiscount(appointmentItem.orderItem.discount)
                    }
                    versionPetAndServices={versionPetAndServices.edit}
                    petWithoutSelect={true}
                    petsUsed={usedPetIds}
                    roomUnitId={
                        appointmentItem.appointment.roomUnitId ||
                        appointmentItem.appointment?.tasks[0]?.feeding?.roomUnitId ||
                        appointmentItem.appointment?.tasks[0]?.medication?.roomUnitId
                    }
                    servicesUsed={usedPetServicesIds}
                    loading={saving}
                    flow={
                        businessAreaId === 'transportation'
                            ? 'transportation'
                            : isEvaluation || isDental || (props.recurrentInfo?.frecuency && !inRecurringSeries)
                            ? 'grooming'
                            : (businessAreaId as 'grooming' | 'daycare' | 'boarding' | 'walking' | 'training')
                    }
                    transportationType={transportationType}
                    hideRemove={props.hideRemove}
                    nights={appointmentItem.appointment.nightsCount}
                    onBack={props.onClose}
                    onBook={saveServiceHandler}
                    onRemove={props.onDelete}
                    onSaveDaycare={saveDaycareHandler}
                    isEvaluation={isEvaluation}
                    onSaveBoarding={saveBoardingHandler}
                    onSaveTransportation={saveTransportationHandler}
                    onSaveWalking={saveWalkingHandler}
                    onSaveTraining={saveTrainingHandler}
                />
            ) : (
                <ComplementaryItem
                    {...complementaryItemProps}
                    versionPetAndServices={versionPetAndServices.edit}
                    petWithoutSelect={true}
                    loading={saving}
                    itemsUsed={getItemsUsed(props.order, props.orderItemId)}
                    dates={getDates(props.order, props.orderItemId)}
                    onBack={props.onClose}
                    onBook={saveOrderItemHandler}
                    onRemove={props.onDelete}
                />
            )}
            {recurrentRescheduleConfirmation.show && (
                <ConfirmRecurringRescheduleDialog
                    open={true}
                    upcomingAppointments={recurrentRescheduleConfirmation.upcomingAppoinmentsCount ?? 0}
                    zIndex={props.zIndex}
                    onCancel={() => closeRecurrentRescheduleConfirmation()}
                    onUpdate={type => {
                        if (type === 'single') {
                            recurrentRescheduleConfirmation.singleRescheduleInfo
                                ? rescheduleSingleAppointment(recurrentRescheduleConfirmation.singleRescheduleInfo)
                                : closeRecurrentRescheduleConfirmation();
                        } else {
                            recurrentRescheduleConfirmation.recurrentRescheduleInfo
                                ? rescheduleRecurrentAppointment(
                                      recurrentRescheduleConfirmation.recurrentRescheduleInfo
                                  )
                                : closeRecurrentRescheduleConfirmation();
                        }
                    }}
                />
            )}
        </>
    );
};

export default EditOrderItem;
